import {
  getAccountingBanksList,
  getAccountingBanksListWithSpreadFilter,
  getBankAndCashOnHand,
  getBankList,
  getBankTransactions,
  getBusinnessCashOnHand,
  getBusinnessRebates,
  getLedgerAccountList,
  getRebatesAccount,
  getRebatesList,
  getRebatesSupplierList,
  getBillsLedgerAccount,
  getVatLogs,
  getChartOfAccounts,
  getAccountTypes,
  getJournalEntries,
  getJournalEntryDetails,
  getChartAccountTransactions,
  getChartAccountDetails,
  getAllChartOfAccounts,
  getChartAccountTransactionDetails,
  getGeneralLedgerSummary,
  getProfitAndLossReport,
  getTrialBalanceReport,
  getBalanceSheetReport,
  getCashFlowReport,
  getGeneralLedgerTransactions,
} from "../../../services/accounting";
import { useQuery } from "react-query";
import { fetchBankList } from "../../../services/transfers";

export const useGetAccountingBanksList = ({ queries }) => {
  return useQuery(
    ["getAccountingBanksList", queries],
    () => getAccountingBanksList(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetAccountingBanksListWithSpreadFilter = ({
  queryParams = {},
  filterParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getAccountingBanksListWithSpreadFilter", queryParams, filterParams],
    () =>
      getAccountingBanksListWithSpreadFilter(queryParams).then(
        (res) => res?.data,
      ),
    {
      staleTime: Infinity,
      enabled: isEnabled,
      retry: () => {},
    },
  );
};

export const useGetAccountingBanksListConditionally = (
  queryParams = {
    isShowBankAccounts: false,
    isShowCashOnHandAccount: false,
    isShowRebateAccount: false,
  },
) => {
  const { data: bankAccounts = {}, isLoading: isLoadingBankAccounts } =
    useGetAccountingBanksListWithSpreadFilter({
      queryParams: { account_type: "bank_account" },
      filterParams: queryParams,
      isEnabled: queryParams?.isShowBankAccounts ? true : false,
    });

  const {
    data: cashOnHandAccounts = {},
    isLoading: isLoadingCashOnHandAccounts,
  } = useGetAccountingBanksListWithSpreadFilter({
    queryParams: { account_type: "cash_on_hand" },
    filterParams: queryParams,
    isEnabled: queryParams?.isShowCashOnHandAccount ? true : false,
  });

  const { data: rebateAccounts = {}, isLoading: isLoadingRebateAccounts } =
    useGetAccountingBanksListWithSpreadFilter({
      queryParams: { account_type: "rebates" },
      filterParams: queryParams,
      isEnabled: queryParams?.isShowRebateAccount ? true : false,
    });

  return {
    isLoading:
      isLoadingBankAccounts ||
      isLoadingCashOnHandAccounts ||
      isLoadingRebateAccounts,
    data: [
      ...(bankAccounts?.data || []),
      ...(cashOnHandAccounts?.data || []),
      ...(rebateAccounts?.data || []),
    ],
    cashOnHandAccounts: cashOnHandAccounts?.data || [],
    bankAccounts: bankAccounts?.data || [],
    rebateAccounts: rebateAccounts?.data || [],
  };
};

export const useGetBankTransactionList = ({ queries }) => {
  return useQuery(
    ["getBankTransactionList", queries],
    () => getBankTransactions(queries).then((res) => res?.data),
    {
      //staleTime: Infinity,
    },
  );
};

export const useGetBankList = (queryParams = {}) => {
  return useQuery(
    ["getBankList", queryParams],
    () => getBankList(queryParams).then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetInvoiceBankAccounts = ({
  store_id,
  query = "",
  isEnabled = true,
}) => {
  const queryParams = { active: true, per_page: 100 };
  if (store_id) queryParams.store_id = store_id;
  if (query) queryParams.query = query;

  return useQuery(
    ["getInvoiceBankAccounts", store_id, query],
    () => getBankList(queryParams).then((res) => res?.data),
    {
      enabled: isEnabled,
      staleTime: 1000 * 60 * 5,
    },
  );
};

export const useGetLedgerAccountList = () => {
  return useQuery(
    ["getLedgerAccountList"],
    () => getLedgerAccountList().then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetBusinessCashOnHand = () => {
  return useQuery(
    ["getBusinnessCashOnHand"],
    () => getBusinnessCashOnHand().then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetBusinessRebates = () => {
  return useQuery(
    ["getBusinnessRebates"],
    () => getBusinnessRebates().then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetBankNameList = ({ isEnabled = true }) => {
  return useQuery(
    ["getBankNameList"],
    () => fetchBankList("").then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetBankAndCashOnHand = (store_id) => {
  return useQuery(
    ["getBankAndCashOnHand", store_id],
    () => getBankAndCashOnHand(store_id).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: !!store_id,
    },
  );
};

export const useGetRebatesList = () => {
  return useQuery(
    ["getRebatesList"],
    () => getRebatesList().then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetRebatesSupplierList = (supplier_id) => {
  return useQuery(
    ["getRebatesSupplierList", supplier_id],
    () => getRebatesSupplierList(supplier_id).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: !!supplier_id,
    },
  );
};

export const useGetRebatesAccount = (queries = "") => {
  return useQuery(
    ["getRebatesAccount", queries],
    () => getRebatesAccount(queries).then((res) => res?.data),
    {
      //staleTime: Infinity,
    },
  );
};

export const useGetBillsLedgerAccount = ({
  queryParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getBillsLedgerAccount", queryParams],
    () => getBillsLedgerAccount(queryParams).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetVatLogs = ({
  queries = "",
  queryParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getVatLogs", queries, queryParams],
    () => getVatLogs(queries, queryParams).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetChartOfAccounts = ({
  queries = "",
  queryParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getChartOfAccounts", queries, queryParams],
    () => getChartOfAccounts(queries, queryParams).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetAccountTypes = () => {
  return useQuery(
    ["useGetAccountTypes"],
    () => getAccountTypes().then((res) => res?.data?.data),
    {
      enabled: true,
      staleTime: Infinity,
    },
  );
};

export const useGetJournalEntries = ({
  queries = "",
  queryParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getJournalEntries", queries, queryParams],
    () => getJournalEntries(queries, queryParams).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetJournalEntryDetails = (journalReference) => {
  return useQuery(
    ["getJournalEntryDetails", journalReference],
    () =>
      getJournalEntryDetails(journalReference).then((res) => res?.data?.data),
    {
      enabled: !!journalReference,
      staleTime: Infinity,
    },
  );
};

export const useGetGeneralLedgerTransactions = ({
  queries,
  enabled = true,
}) => {
  return useQuery(
    ["getGeneralLedgerTransactions", queries],
    () => getGeneralLedgerTransactions(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled,
    },
  );
};

export const useGetChartAccountTransactions = ({ queries, enabled = true }) => {
  return useQuery(
    ["getChartAccountTransactions", queries],
    () => getChartAccountTransactions(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled,
    },
  );
};

export const useGetChartAccountDetails = (accountId) => {
  return useQuery(
    ["getChartAccountDetails", accountId],
    () => getChartAccountDetails(accountId).then((res) => res?.data?.data),
    {
      enabled: !!accountId,
      staleTime: Infinity,
    },
  );
};

export const useGetAllChartOfAccounts = (queryParams = {}) => {
  return useQuery(
    ["getAllChartOfAccounts", queryParams],
    () => getAllChartOfAccounts(queryParams).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: true,
    },
  );
};

export const useGetChartAccountTransactionDetails = (id) => {
  return useQuery(
    ["getChartAccountTransactionDetails", id],
    () => getChartAccountTransactionDetails(id).then((res) => res?.data),
    {
      enabled: !!id,
      staleTime: Infinity,
    },
  );
};

export const useGetGeneralLedgerSummary = ({ queries, enabled = true }) => {
  const sanitizedQueries = { ...queries };
  delete sanitizedQueries.page;
  delete sanitizedQueries.per_page;
  return useQuery(
    ["getGeneralLedgerSummary", queries],
    () => getGeneralLedgerSummary(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled,
    },
  );
};

export const useGetProfitAndLossReport = ({ queries, enabled = true }) => {
  return useQuery(
    ["getProfitAndLossReport", queries],
    () => getProfitAndLossReport(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled,
    },
  );
};

export const useGetTrialBalanceReport = ({ queries, enabled = true }) => {
  return useQuery(
    ["getTrialBalanceReport", queries],
    () => getTrialBalanceReport(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled,
    },
  );
};

export const useGetBalanceSheetReport = ({ queries, enabled = true }) => {
  return useQuery(
    ["getBalanceSheetReport", queries],
    () => getBalanceSheetReport(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled,
    },
  );
};

export const useGetCashFlowReport = ({ queries, enabled = true }) => {
  return useQuery(
    ["getCashFlowReport", queries],
    () => getCashFlowReport(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled,
    },
  );
};
